import { useEffect, useState } from 'react'
import { AlertTriangle, Eye, FileMinus2, Search, Sparkles } from 'lucide-react'
import { ExportButton } from '@/app/export/ExportButton'
import { ExportEstimate } from '@/app/export/ExportEstimate'
import { largestExportCanvasArea, planExport } from '@/core/export/planExport'
import { canShareFiles, createShareCapabilityFiles } from '@/core/export/share'
import {
  CUSTOM_SIZE_MAX,
  CUSTOM_SIZE_MIN,
  ExportAction,
  ExportFormat,
  ResizeMode,
} from '@/core/export/types'
import { JPEG_QUALITY_MAX, JPEG_QUALITY_MIN } from '@/core/export/validate'
import { ReportingStage, surfaceError } from '@/core/reporting'
import { cachedMaxCanvasArea, maxSquareCanvasSide, probeMaxCanvasArea } from '@/core/render/canvasLimits'
import { useEditSession } from '@/core/store/editSession'
import { MorePanelId } from '@/core/store/uiState'
import { TEST_IDS } from '@/lib/testIds'
import { QUALITY_TOOL } from '@/tools/quality'
import { ChoiceGroup, ChoiceTile } from '@/ui/ChoiceTile'
import { IconSlider } from '@/ui/IconSlider'
import { FileIllustration, SizeIllustration } from '@/ui/illustrations/PanelIllustrations'
import { MoreSection } from '@/ui/MoreSection'
import { Button } from '@/ui/shadcn/button'

const QualityOverlay = QUALITY_TOOL.Overlay

function useMaxCanvasArea(): number | null {
  const [maxCanvasArea, setMaxCanvasArea] = useState(cachedMaxCanvasArea)
  useEffect(() => {
    let cancelled = false
    void probeMaxCanvasArea()
      .then((area) => {
        if (!cancelled) setMaxCanvasArea(area)
      })
      .catch((error: unknown) => {
        if (!cancelled) surfaceError(error, 'Could not determine this device\'s export limit.', {
          stage: ReportingStage.Application,
        })
      })
    return () => {
      cancelled = true
    }
  }, [])
  return maxCanvasArea
}

const FORMAT_LABELS: Readonly<Record<ExportFormat, string>> = {
  [ExportFormat.Jpeg]: 'JPEG',
  [ExportFormat.Png]: 'PNG',
}

const SIZE_LABELS: Readonly<Record<ResizeMode, string>> = {
  [ResizeMode.InstagramTarget]: 'Instagram',
  [ResizeMode.Original]: 'Original',
  [ResizeMode.Custom]: 'Custom',
}

export function ExportPanel() {
  const maxCanvasArea = useMaxCanvasArea()
  const document = useEditSession((state) => state.document)
  const sources = useEditSession((state) => state.sources)
  const exportOptions = useEditSession((state) => state.exportOptions)
  const setExportOptions = useEditSession((state) => state.setExportOptions)
  const setMoreSectionOpen = useEditSession((state) => state.setMoreSectionOpen)
  const setPreviewOpen = useEditSession((state) => state.setPreviewOpen)
  const setQualityViewOpen = useEditSession((state) => state.setQualityViewOpen)

  if (!document) throw new Error('Export panel requires a loaded document')
  const plan = planExport(document, sources, exportOptions)
  const output = plan.outputs[0]
  if (!output) throw new Error('Document export plan did not produce an output')
  const originalPlan = planExport(document, sources, {
    ...exportOptions,
    resizeMode: ResizeMode.Original,
    customLongestSide: null,
  })
  const originalOutput = originalPlan.outputs[0]
  if (!originalOutput) throw new Error('Original export plan did not produce an output')
  const shareAvailable = canShareFiles(createShareCapabilityFiles(plan.outputs.length, exportOptions.format))
  const originalExceedsCanvasLimit = maxCanvasArea !== null
    && largestExportCanvasArea(originalPlan) > maxCanvasArea
  const dimensions = `${output.targetSize.width} × ${output.targetSize.height}`

  const updateResizeMode = (resizeMode: ResizeMode) => {
    if (!Object.values(ResizeMode).includes(resizeMode)) throw new Error(`Unknown resize mode selected: "${String(resizeMode)}"`)
    setExportOptions(resizeMode === ResizeMode.Custom
      ? {
          resizeMode,
          customLongestSide: Math.min(
            CUSTOM_SIZE_MAX,
            Math.max(CUSTOM_SIZE_MIN, originalOutput.targetSize.width, originalOutput.targetSize.height),
          ),
        }
      : { resizeMode, customLongestSide: null })
    if (resizeMode === ResizeMode.Custom) setMoreSectionOpen(MorePanelId.Export, true)
  }

  return (
    <section className="space-y-5" aria-labelledby="export-heading">
      <h2 id="export-heading" className="font-semibold">Export</h2>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">File</h3>
        <ChoiceGroup mode="single" label="Export file format" className="grid-cols-2">
          {Object.values(ExportFormat).map((format) => (
            <ChoiceTile
              key={format}
              ariaLabel={FORMAT_LABELS[format]}
              label={FORMAT_LABELS[format]}
              illustration={<FileIllustration format={format} />}
              selected={exportOptions.format === format}
              data-testid={TEST_IDS.exportFormat(format)}
              onClick={() => setExportOptions({ format })}
            />
          ))}
        </ChoiceGroup>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Size</h3>
        <ChoiceGroup mode="single" label="Export size" className="grid-cols-3" data-testid={TEST_IDS.resizeToggle}>
          {[ResizeMode.InstagramTarget, ResizeMode.Original, ResizeMode.Custom].map((mode) => (
            <ChoiceTile
              key={mode}
              ariaLabel={SIZE_LABELS[mode]}
              label={SIZE_LABELS[mode]}
              illustration={<SizeIllustration mode={mode} />}
              selected={exportOptions.resizeMode === mode}
              data-testid={TEST_IDS.resizeMode(mode)}
              onClick={() => updateResizeMode(mode)}
            />
          ))}
        </ChoiceGroup>
        {originalExceedsCanvasLimit ? (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Too large for this device — choose Instagram or Custom ≤ {maxSquareCanvasSide(maxCanvasArea)} px.
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-1 rounded-lg bg-muted px-3 py-2">
        <ExportEstimate dimensions={dimensions} className="min-w-0 flex-1 justify-start" />
        {exportOptions.format === ExportFormat.Jpeg ? (
          <Button type="button" variant="ghost" size="icon" className="size-8" data-testid={TEST_IDS.qualityCompare} aria-label="Compare export quality" onClick={() => setQualityViewOpen(true)}>
            <Search aria-hidden="true" />
          </Button>
        ) : null}
        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Open Instagram preview" onClick={() => setPreviewOpen(true)}>
          <Eye aria-hidden="true" />
        </Button>
        <QualityOverlay />
      </div>

      <MoreSection id={MorePanelId.Export}>
        {exportOptions.resizeMode === ResizeMode.Custom ? (
          <label className="grid grid-cols-[1fr_7rem] items-center gap-3 text-sm font-medium">
            Longest side
            <input
              type="number"
              min={CUSTOM_SIZE_MIN}
              max={CUSTOM_SIZE_MAX}
              step={1}
              value={exportOptions.customLongestSide ?? ''}
              data-testid={TEST_IDS.customLongestSide}
              className="h-9 rounded-md border bg-transparent px-3 text-right tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onChange={(event) => setExportOptions({ customLongestSide: event.currentTarget.valueAsNumber })}
              aria-label="Custom export longest side"
            />
          </label>
        ) : null}
        {exportOptions.format === ExportFormat.Jpeg ? (
          <IconSlider
            min={JPEG_QUALITY_MIN}
            max={JPEG_QUALITY_MAX}
            step={0.01}
            value={exportOptions.jpegQuality}
            startIcon={<FileMinus2 className="size-4" />}
            endIcon={<Sparkles className="size-4" />}
            formatValue={(value) => `${Math.round(value * 100)}%`}
            data-testid={TEST_IDS.jpegQualitySlider}
            aria-label="JPEG quality"
            onValueChange={(jpegQuality) => setExportOptions({ jpegQuality })}
          />
        ) : null}
      </MoreSection>

      {plan.warnings && plan.warnings.length > 0 ? (
        <div role="status" data-testid={TEST_IDS.exportWarnings} className="flex gap-2 rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2.5 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          <div className="space-y-1">{plan.warnings.map((warning) => <p key={warning}>{warning}</p>)}</div>
        </div>
      ) : null}

      {shareAvailable ? (
        <ExportButton className="w-full" action={ExportAction.SaveFiles} secondary testId={TEST_IDS.exportSave} />
      ) : plan.outputs.length > 1 ? (
        <ExportButton className="w-full" action={ExportAction.SaveArchive} label="Download as zip" secondary testId={TEST_IDS.exportDownloadZip} />
      ) : null}
    </section>
  )
}
